// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { CheckOutlined, SendOutlined } from '@ant-design/icons';
import { type FactorType, type ImprUser, isAdmin, type User } from '@api/types';
import { isCurrentUser, useSession } from '@common/authorization/context/Session';
import { Button, ImprChip, ImprList, type ImprListProps } from '@imprivata-cloud/components';
import { Popover, type TableColumnsType, Tooltip } from 'antd';
import type { TableRowSelection } from 'antd/lib/table/interface';
import { useTranslation } from 'react-i18next';
import DeleteImg from '../../../../assets/icons/delete.svg?react';
import EditIcon from '../../../../assets/icons/edit.svg?react';
import MenuIcon from '../../../../assets/icons/kabob_menu.svg?react';
import UnlockImg from '../../../../assets/unlock.svg?react';
import mapFactorTypeToNameAndIcon from '../../../../common/authMethodInfo/authMethodInfo';
import UserListRowMenu from './UserListRowMenu';
import './ImprUserList.less';

export interface MenuButtonProps {
  enableEdit?: boolean;
  editUser?: (userId: string) => void;
  deleteUser?: (userId: string) => void;
  unlockUser?: (userId: string, hasAccountLock: boolean, factorTypes: string[]) => void;
  adminDeleteDisabled?: boolean;
  enableUnlock?: (user: ImprUser | User) => boolean;
  isPending?: boolean;
  isUnlocked?: (userId: string) => boolean;
  onInviteUser?: (id: string) => void;
}

const TRANSLATION_ROOT = 'portal.settings.identity-users';

const PERMANENT_LOCK = '9999-12-31T23:59:59Z';

const formatLockedUntil = (isoDate: string): string =>
  new Date(isoDate)
    .toISOString()
    .replace('T', ' ')
    .replace(/\.\d+Z$/, ' UTC');

const ImprUserList = <T extends Record<string, unknown>>({
  rowKey,
  ...props
}: ImprListProps<T> & MenuButtonProps) => {
  const { userInfo: sessionUserInfo } = useSession();
  const { t } = useTranslation();

  const columns: TableColumnsType<Record<string, unknown>> = [
    {
      className: 'list-item-left-col',
      render: (_, data) => {
        const user: ImprUser | User = data as User;
        const upn = (data as User).upn || (data as ImprUser).username;
        return (
          <>
            <div data-testid="list-item-primary-text" className="list-item-primary-text">
              {user?.displayName}
            </div>
            <div data-testid="list-item-secondary-text" className="list-item-secondary-text">
              {upn}
            </div>
          </>
        );
      },
    },
    {
      render: (_, data) => {
        const user: ImprUser | User = data as ImprUser;
        return (
          <>{isAdmin(user) && <ImprChip label={t('common.admin')} className="admin-chip" />}</>
        );
      },
    },
    {
      render: (_, data) => {
        const user: ImprUser | User = data as ImprUser;
        const userId = user?.userId;
        if (!userId) {
          return null;
        }
        return (
          <div className="list-item-right">
            {/* Unlock user */}
            {props.enableUnlock?.(user) &&
              (() => {
                const unlocked = props.isUnlocked?.(userId) ?? false;

                return (
                  <Tooltip
                    title={
                      unlocked ? null : (
                        <>
                          {user.lockedUntil &&
                            new Date(user.lockedUntil).getTime() > Date.now() && (
                              <div>
                                {t(`${TRANSLATION_ROOT}.unlock.unlock-account-tooltip`, {
                                  lockedUntil: formatLockedUntil(user.lockedUntil),
                                })}
                              </div>
                            )}
                          {user.factorLockouts
                            ?.filter(
                              lockout =>
                                lockout.lockedUntil === PERMANENT_LOCK ||
                                new Date(lockout.lockedUntil).getTime() > Date.now(),
                            )
                            .map(lockout => {
                              const factorName = mapFactorTypeToNameAndIcon(
                                lockout.factorType as FactorType,
                                t,
                              ).name;
                              const isPermanent = lockout.lockedUntil === PERMANENT_LOCK;

                              return (
                                <div key={lockout.factorType}>
                                  {isPermanent
                                    ? t(
                                        `${TRANSLATION_ROOT}.unlock.unlock-factor-permanent-tooltip`,
                                        { factorName },
                                      )
                                    : t(`${TRANSLATION_ROOT}.unlock.unlock-factor-tooltip`, {
                                        factorName,
                                        lockedUntil: formatLockedUntil(lockout.lockedUntil),
                                      })}
                                </div>
                              );
                            })}
                        </>
                      )
                    }
                  >
                    <Button
                      key={`unlock-${userId}`}
                      icon={unlocked ? <CheckOutlined /> : <UnlockImg />}
                      disabled={props.isPending || unlocked}
                      label={
                        unlocked
                          ? t(`${TRANSLATION_ROOT}.unlock.unlocked-button`)
                          : t(`${TRANSLATION_ROOT}.unlock.unlock-button`)
                      }
                      type="text"
                      onClick={() => {
                        const hasAccountLock = !!user.lockedUntil;
                        const factorTypes = user.factorLockouts?.map(f => f.factorType) ?? [];
                        props.unlockUser?.(userId, hasAccountLock, factorTypes);
                      }}
                      aria-label="unlock-user-button"
                      style={unlocked ? { color: '#30961F' } : undefined}
                    />
                  </Tooltip>
                );
              })()}

            {/* Invite user */}
            {props.onInviteUser && (
              <Button
                key={`invite-${userId}`}
                aria-label="button-invite-user"
                type="text"
                disabled={props.isPending}
                icon={<SendOutlined />}
                onClick={() => props.onInviteUser?.(userId)}
                label={t(`${TRANSLATION_ROOT}.invite-user`)}
              />
            )}

            {/* Edit row */}
            {props.enableEdit && (
              <Button
                key={`edit-${userId}`}
                icon={<EditIcon fill={'#566579'} />}
                label={t('common.edit')}
                type="text"
                onClick={() => {
                  if (props.editUser) {
                    props.editUser(userId);
                  }
                }}
                aria-label="edit-user-button"
              />
            )}
            {/* Delete row */}
            {props.deleteUser && (
              <Button
                key={`delete-${userId}`}
                icon={<DeleteImg />}
                label={t('common.remove')}
                type="text"
                onClick={() => props.deleteUser?.(userId)}
                aria-label="delete-user-button"
                disabled={
                  isCurrentUser((data as ImprUser).username, sessionUserInfo) ||
                  (props.adminDeleteDisabled && isAdmin(data as ImprUser))
                }
              />
            )}

            {/* 3 dots menu */}
            <Popover
              content={<UserListRowMenu userId={userId} />}
              trigger="click"
              placement="bottomRight"
              destroyTooltipOnHide={true}
            >
              <Button type="text" icon={<MenuIcon />} aria-label="row-menu" />
            </Popover>
          </div>
        );
      },
    },
  ];

  return (
    <div className="impr-user-list">
      <ImprList
        sticky
        enableSearch={props.enableSearch}
        columns={columns}
        dataSource={props.dataSource}
        rowSelection={props.rowSelection as TableRowSelection}
        showHeader={false}
        sortLabel={props.sortLabel}
        customEmptyListNode={props.customEmptyListNode}
        pagination={false}
        rowKey={rowKey as string}
        handleSearch={props.handleSearch}
        headerExtraComponents={props.headerExtraComponents}
        headerExtraComponentsLeft={props.headerExtraComponentsLeft}
        onSearchChange={props.onSearchChange}
      />
    </div>
  );
};

export default ImprUserList;
